import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from "recharts";

const fmt = (n) =>
  n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pct = (n) => (n == null ? "—" : Number(n).toFixed(1) + "%");

const delta = (current, prior) => {
  if (prior == null || prior === 0) return null;
  return (((current - prior) / prior) * 100).toFixed(1);
};

export default function App() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const { data, error } = await supabase
        .from("cfo_snapshots")
        .select("*")
        .eq("company_name", "Demo Co")
        .order("report_date", { ascending: false })
        .limit(12);
      if (error) setError(error.message);
      else setRows(data || []);
      setLoading(false);
    }
    fetchData();
  }, []);

  const latest = rows[0];
  const prior = rows[1];
  const chartData = [...rows].reverse().map((r) => ({
    week: r.report_date?.slice(5),
    Revenue: Number(r.revenue),
    "GP%": Number(r.gp_margin_pct),
    "Net Income": Number(r.net_income),
  }));

  if (loading) return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <p style={{ color: "#64748b", marginTop: 16, fontFamily: "'DM Sans', sans-serif" }}>Loading dashboard...</p>
    </div>
  );

  if (error) return (
    <div style={styles.center}>
      <p style={{ color: "#ef4444", fontFamily: "'DM Sans', sans-serif" }}>Error: {error}</p>
    </div>
  );

  const revDelta = latest && prior ? delta(latest.revenue, prior.revenue) : null;
  const netDelta = latest && prior ? delta(latest.net_income, prior.net_income) : null;

  return (
    <div style={styles.app}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* Header */}
      <header style={styles.header}>
        <div>
          <div style={styles.logo}>McGinnis AI</div>
          <div style={styles.headerSub}>CFO Dashboard · Demo Co</div>
        </div>
        <div style={styles.liveBadge}>● Live</div>
      </header>

      {/* Period bar */}
      <div style={styles.periodBar}>
        <span style={styles.periodLabel}>Latest report week</span>
        <span style={styles.periodValue}>{latest?.report_date || "—"}</span>
      </div>

      {/* Metric cards */}
      <div style={styles.metrics}>
        {[
          { label: "Revenue", value: fmt(latest?.revenue), delta: revDelta, sub: "vs prior week" },
          { label: "Gross profit", value: fmt(latest?.gross_profit), delta: null, sub: pct(latest?.gp_margin_pct) + " margin" },
          { label: "Operating expenses", value: fmt(latest?.operating_expenses), delta: null, sub: "this week" },
          { label: "Net income", value: fmt(latest?.net_income), delta: netDelta, sub: "vs prior week" },
        ].map((m) => (
          <div key={m.label} style={styles.metricCard}>
            <div style={styles.metricLabel}>{m.label}</div>
            <div style={styles.metricValue}>{m.value}</div>
            <div style={styles.metricSub}>
              {m.delta !== null && (
                <span style={{ color: parseFloat(m.delta) >= 0 ? "#10b981" : "#ef4444", marginRight: 6 }}>
                  {parseFloat(m.delta) >= 0 ? "▲" : "▼"} {Math.abs(m.delta)}%
                </span>
              )}
              <span style={{ color: "#94a3b8" }}>{m.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={styles.charts}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Revenue trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "DM Sans" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "DM Sans" }} tickFormatter={(v) => "$" + (v / 1000).toFixed(0) + "k"} />
              <Tooltip formatter={(v) => ["$" + v.toLocaleString(), "Revenue"]} labelStyle={{ fontFamily: "DM Sans" }} contentStyle={{ fontFamily: "DM Sans", borderRadius: 8, border: "0.5px solid #e2e8f0" }} />
              <Line type="monotone" dataKey="Revenue" stroke="#1E3A5F" strokeWidth={2} dot={{ r: 3, fill: "#1E3A5F" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>GP% trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "DM Sans" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "DM Sans" }} tickFormatter={(v) => v + "%"} domain={[40, 60]} />
              <Tooltip formatter={(v) => [v + "%", "GP Margin"]} labelStyle={{ fontFamily: "DM Sans" }} contentStyle={{ fontFamily: "DM Sans", borderRadius: 8, border: "0.5px solid #e2e8f0" }} />
              <Bar dataKey="GP%" fill="#2E75B6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Narrative */}
      {latest?.narrative_summary && (
        <div style={styles.narrativeCard}>
          <div style={styles.narrativeHeader}>
            <span style={styles.narrativeIcon}>AI</span>
            <span style={styles.cardTitle}>Executive summary</span>
          </div>
          <p style={styles.narrativeText}>{latest.narrative_summary}</p>
        </div>
      )}

      {/* History table */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Weekly history</div>
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Week", "Revenue", "GP%", "Op. expenses", "Net income", "WoW"].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const prev = rows[i + 1];
                const d = prev ? delta(row.revenue, prev.revenue) : null;
                return (
                  <tr key={row.id} style={i % 2 === 0 ? {} : { background: "#f8fafc" }}>
                    <td style={styles.td}>{row.report_date}</td>
                    <td style={styles.td}>{fmt(row.revenue)}</td>
                    <td style={styles.td}>{pct(row.gp_margin_pct)}</td>
                    <td style={styles.td}>{fmt(row.operating_expenses)}</td>
                    <td style={styles.td}>{fmt(row.net_income)}</td>
                    <td style={styles.td}>
                      {d !== null ? (
                        <span style={{
                          ...styles.pill,
                          background: parseFloat(d) >= 0 ? "#d1fae5" : "#fee2e2",
                          color: parseFloat(d) >= 0 ? "#065f46" : "#991b1b",
                        }}>
                          {parseFloat(d) >= 0 ? "+" : ""}{d}%
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        Automated by McGinnis AI Automation · mcginnisaiautomation.com
      </div>
    </div>
  );
}

const styles = {
  app: {
    fontFamily: "'DM Sans', sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: "0 1rem 3rem",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  center: {
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    minHeight: "100vh",
  },
  spinner: {
    width: 32, height: 32,
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #1E3A5F",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "1.5rem 0 1rem",
    borderBottom: "0.5px solid #e2e8f0",
    marginBottom: "1.25rem",
  },
  logo: { fontSize: 18, fontWeight: 600, color: "#1E3A5F", letterSpacing: "-0.3px" },
  headerSub: { fontSize: 13, color: "#94a3b8", marginTop: 2 },
  liveBadge: {
    fontSize: 12, fontWeight: 500,
    color: "#065f46", background: "#d1fae5",
    padding: "4px 12px", borderRadius: 99,
  },
  periodBar: {
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: "1.25rem",
  },
  periodLabel: { fontSize: 13, color: "#94a3b8" },
  periodValue: { fontSize: 13, fontWeight: 500, color: "#1E3A5F", fontFamily: "'DM Mono', monospace" },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10, marginBottom: "1.25rem",
  },
  metricCard: {
    background: "#ffffff",
    border: "0.5px solid #e2e8f0",
    borderRadius: 12, padding: "1rem",
  },
  metricLabel: { fontSize: 12, color: "#94a3b8", marginBottom: 6 },
  metricValue: { fontSize: 20, fontWeight: 600, color: "#0f172a", letterSpacing: "-0.5px", fontFamily: "'DM Mono', monospace" },
  metricSub: { fontSize: 12, marginTop: 4, display: "flex", alignItems: "center" },
  charts: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)",
    gap: 12, marginBottom: "1.25rem",
  },
  card: {
    background: "#ffffff",
    border: "0.5px solid #e2e8f0",
    borderRadius: 12, padding: "1rem 1.25rem",
    marginBottom: "1.25rem",
  },
  cardTitle: { fontSize: 12, fontWeight: 500, color: "#94a3b8", marginBottom: "1rem", textTransform: "uppercase", letterSpacing: "0.5px" },
  narrativeCard: {
    background: "#ffffff",
    border: "0.5px solid #e2e8f0",
    borderLeft: "3px solid #1E3A5F",
    borderRadius: 12, padding: "1rem 1.25rem",
    marginBottom: "1.25rem",
  },
  narrativeHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: "0.75rem" },
  narrativeIcon: {
    fontSize: 10, fontWeight: 600, color: "#ffffff",
    background: "#1E3A5F", padding: "3px 7px", borderRadius: 6,
  },
  narrativeText: { fontSize: 14, color: "#334155", lineHeight: 1.75 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "8px 12px", textAlign: "left",
    color: "#94a3b8", fontWeight: 500, fontSize: 12,
    borderBottom: "0.5px solid #e2e8f0",
    textTransform: "uppercase", letterSpacing: "0.4px",
  },
  td: { padding: "10px 12px", color: "#334155", borderBottom: "0.5px solid #f1f5f9" },
  pill: {
    display: "inline-block", fontSize: 11, fontWeight: 500,
    padding: "2px 8px", borderRadius: 99,
  },
  footer: {
    textAlign: "center", fontSize: 12,
    color: "#cbd5e1", padding: "2rem 0 0",
  },
};
